import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavbarChevronIcon from '../layout/NavbarChevronIcon';
import DetailNavbarLeft from '../shared/DetailNavbarLeft';
import NavbarCloseButton from '../shared/NavbarCloseButton';
import { findSiblingIndex } from '../orders/orderdetail/orderDetailNavState';
import {
  DEFAULT_SHIPMENT_DETAIL_FROM_PATH,
  parseShipmentDetailNavState,
} from './shipmentDetailNavState';
import '../orders/orderdetail/pick-list.css';
import '../labels/PrintBoxLabel.css';

interface ShipmentDetailNavbarProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly backPath?: string;
  readonly closeAriaLabel?: string;
  readonly showSiblingNav?: boolean;
}

function ShipmentDetailNavbar({
  title,
  subtitle,
  backPath,
  closeAriaLabel = 'Close',
  showSiblingNav = true,
}: ShipmentDetailNavbarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navState = parseShipmentDetailNavState(location.state);

  const siblingIndex = findSiblingIndex(navState.siblingShipmentIds, shipmentId);
  const hasSiblings = showSiblingNav && navState.siblingShipmentIds.length > 1 && siblingIndex >= 0;
  const prevSiblingId =
    hasSiblings && siblingIndex > 0 ? navState.siblingShipmentIds[siblingIndex - 1] : null;
  const nextSiblingId =
    hasSiblings && siblingIndex < navState.siblingShipmentIds.length - 1
      ? navState.siblingShipmentIds[siblingIndex + 1]
      : null;

  const handleClose = (): void => {
    if (backPath) {
      navigate(backPath, { state: navState });
      return;
    }
    navigate(navState.fromPath || DEFAULT_SHIPMENT_DETAIL_FROM_PATH);
  };

  const goToSibling = (siblingId: string): void => {
    navigate(`/receive/shipment/${siblingId}`, { replace: true, state: navState });
  };

  return (
    <nav className="order-detail-navbar shipment-detail-navbar" aria-label="Shipment details">
      <DetailNavbarLeft />
      <div className="order-detail-navbar-titles">
        <h1 className="order-detail-navbar-order-number">{title}</h1>
        {subtitle ? (
          <div className="order-detail-navbar-order-name">{subtitle}</div>
        ) : null}
      </div>
      <div className="order-detail-navbar-right">
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => prevSiblingId != null && goToSibling(prevSiblingId)}
          disabled={prevSiblingId == null}
          aria-label="Previous shipment"
        >
          <NavbarChevronIcon direction="left" />
        </button>
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => nextSiblingId != null && goToSibling(nextSiblingId)}
          disabled={nextSiblingId == null}
          aria-label="Next shipment"
        >
          <NavbarChevronIcon direction="right" />
        </button>
        <NavbarCloseButton onClose={handleClose} ariaLabel={closeAriaLabel} />
      </div>
    </nav>
  );
}

export default ShipmentDetailNavbar;
